import styled from "styled-components";
import Breadcrumbs from "../Breadcrumbs";
import UseScroll from "../../../hooks/ui/UseScroll";
import { Link } from "react-scroll";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";
import { languageStore } from "../../../../state/language";
import { menucontext } from "../../../../context/nav/menucontext";
import { getBreadcrumbs } from "../../../../helpers/navigation/breadcrumbs";
import { riseIn } from "../../../../style/styled-components/keyframes/fade";
// 서브 페이지 공통 틀: 상단 비주얼(제목·문구·배경) + 위치 표시 + 본문
// large·noImage 등 기존 props는 그대로 받는다 (large는 더 이상 높이에 영향 없음)
const PageTemplate = ({
  children,
  title,
  image,
  phrase,
  subHeader,
  noImage,
  aone,
  description,
}) => {
  const useScroll = UseScroll();
  const { language } = languageStore();
  const { pathname } = useLocation();
  const t = (x) => (language === "EN" && x.engtitle ? x.engtitle : x.title);
  // 현재 페이지의 상위 메뉴들 (예: 업무분야 › 통관 및 환급 업무)
  const trail = getBreadcrumbs(pathname, "url", menucontext);
  const parents = Array.isArray(trail) ? trail.slice(1).reverse() : [];
  return (
    <Div>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{`${language === "EN" ? "AONE Customs and Trade Service" : `${title ? `${title} - ` : ""}관세법인 에이원`}`} </title>
        <link rel="canonical" href={`https://www.aonecustoms.com${pathname.replace(/\/+$/, "")}`} />
        <link
          rel="icon"
          type="image/x-icon"
          href="./resources/images/logo/aone_logo_only.ico"
        />
      </Helmet>
      {title && (
        <Hero $image={!noImage && image}>
          <div className="inner">
            {parents.length > 0 && (
              <div className="trail">
                {parents.map((x) => (
                  <span key={x.key}>{t(x)}</span>
                ))}
              </div>
            )}
            <h1>{title}</h1>
            {!noImage && phrase && (
              <p>
                {aone && <span>A-ONE, </span>}
                {phrase}
              </p>
            )}
            {subHeader && <p>{subHeader}</p>}
          </div>
        </Hero>
      )}
      <div className="breadcrumbs">
        <Breadcrumbs />
      </div>
      <Page>
        {description && <p className="description">{description}</p>}
        {children}
      </Page>
      <ToTop show={useScroll.y >= 300}>
        <Link to="app" spy smooth aria-label="맨 위로">
          <span aria-hidden="true">↑</span>
          <span>TOP</span>
        </Link>
      </ToTop>
    </Div>
  );
};
export default PageTemplate;
const Div = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  & > div.breadcrumbs {
    width: 100%;
  }
`;
const Hero = styled.section`
  position: relative;
  width: 100%;
  display: flex;
  align-items: flex-end;
  min-height: ${({ $image }) => ($image ? "clamp(17rem, 30vw, 23rem)" : "13rem")};
  color: white;
  background-color: ${({ theme }) => theme.colors.ink};
  background-image: ${({ $image, theme }) =>
    $image
      ? `linear-gradient(90deg, rgba(11, 22, 61, 0.92) 0%, rgba(11, 22, 61, 0.65) 55%, rgba(11, 22, 61, 0.35) 100%), url(${$image})`
      : `radial-gradient(circle at 90% 0%, rgba(1, 101, 172, 0.45), transparent 50%), linear-gradient(135deg, ${theme.colors.ink}, ${theme.colors.dark})`};
  background-size: cover;
  background-position: center;
  & > .inner {
    width: 100%;
    max-width: ${({ theme }) => theme.layout.content};
    margin: 0 auto;
    padding: 3.5rem clamp(1.25rem, 4vw, 2.5rem) 3rem;
    box-sizing: border-box;
    animation: ${riseIn} 0.6s ease-out;
    & > .trail {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      font-size: 0.9rem;
      font-weight: 600;
      color: ${({ theme }) => theme.colors.foreground.highlightinverted};
      margin-bottom: 0.9rem;
      & > span:not(:last-child):after {
        content: "›";
        margin: 0 0.6rem;
        color: rgba(255, 255, 255, 0.45);
      }
    }
    & > h1 {
      margin: 0;
      font-size: clamp(2rem, 4.5vw, 3.25rem);
      line-height: 1.2;
      font-weight: 800;
      letter-spacing: -0.03em;
    }
    & > p {
      margin: 1rem 0 0;
      max-width: 48rem;
      font-size: clamp(1.05rem, 1.8vw, 1.3rem);
      line-height: 1.6;
      color: rgba(255, 255, 255, 0.85);
    }
  }
`;
const Page = styled.div`
  width: 100%;
  max-width: ${({ theme }) => theme.layout.content};
  padding: 0 clamp(1.25rem, 4vw, 2.5rem) 6rem;
  box-sizing: border-box;
  /* 본문에 바로 둔 문단 (첫 문단은 페이지 소개: description 또는 <p>) */
  & > p {
    max-width: 52rem;
    margin: 1.5rem auto 0;
    font-size: clamp(1.05rem, 1.6vw, 1.2rem);
    line-height: 1.9;
    text-align: center;
    color: ${({ theme }) => theme.colors.monotone.dark};
  }
  & > p:first-child {
    margin-top: 4.5rem;
  }
  & > p a {
    color: ${({ theme }) => theme.colors.accent};
    font-weight: 700;
    &:hover {
      text-decoration: underline;
    }
  }
`;
const ToTop = styled.div`
  position: fixed;
  right: clamp(1rem, 2.5vw, 2rem);
  bottom: clamp(1rem, 2.5vw, 2rem);
  z-index: 5;
  & > a {
    cursor: pointer;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 3.25rem;
    height: 3.25rem;
    border-radius: 50%;
    background-color: ${({ theme }) => theme.colors.dark};
    color: white;
    font-size: 0.65rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    line-height: 1.1;
    box-shadow: ${({ theme }) => theme.shadow.md};
    opacity: ${({ show }) => (show ? 1 : 0)};
    transform: translateY(${({ show }) => (show ? 0 : "1rem")});
    pointer-events: ${({ show }) => (show ? "auto" : "none")};
    transition: opacity 0.2s, transform 0.2s, background-color 0.2s;
    & > span:first-child {
      font-size: 1.1rem;
    }
    &:hover {
      background-color: ${({ theme }) => theme.colors.accent};
    }
  }
`;
